/** @file applyBatch: applies a batch atomically to a draft copy; replies, new revision, undo record. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Pose3DError } from '@zilo/pose3d-std/errors/pose3DError';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { toPose3DError } from '@zilo/pose3d-std/errors/toPose3DError';
import type { CommandContext } from '../context/commandContext';
import type { PuppetDoc } from '../puppetDoc/puppetDoc';
import { syncImages } from '../puppetDoc/syncImages';
import type { Batch, BatchReply } from '../types/batch';
import type { Command, CommandName } from '../types/command';
import type { Reply } from '../types/reply';
import type { UndoRecord } from '../types/undo';
import type { CommandDef, CommandOutcome } from './commandDef';
import { COMMAND_HANDLERS } from './commandHandlers';
import { invertBatch } from './invertBatch';

/** What a batch yields: the new document (the input reference when nothing changed), the replies and the undo record. */
export type BatchResult = { doc: PuppetDoc; reply: BatchReply; undo: UndoRecord | null };

type LooseDef = Omit<CommandDef<CommandName>, 'apply'> & { apply: (draft: PuppetDoc, input: unknown, ctx: CommandContext) => CommandOutcome };

const DOCUMENT_ROOTS = ['/commands/', '/puppet', '/clips', '/images'];

function plainError(code: string, params: Record<string, unknown>, path: string, hint?: string): Pose3DError {
  return { name: 'Pose3DError', code, params, path, ...(hint === undefined ? {} : { hint }), message: `${code} at ${path}` };
}

/** The thrown value as a reply error; input paths (`/input/...`) move under `/commands/<i>`, document pointers stay. */
function errorAt(e: unknown, i: number): Pose3DError {
  const err = toPose3DError(e);
  if (err === null) return plainError(ERROR_CODES.INTERNAL, { message: e instanceof Error ? e.message : String(e) }, `/commands/${i}`);
  const own = err.path ?? '';
  const path = DOCUMENT_ROOTS.some((r) => own.startsWith(r)) ? own : `/commands/${i}${own === '/' ? '' : own}`;
  return plainError(err.code, { ...err.params }, path, err.hint);
}

function failed(doc: PuppetDoc, commands: readonly Command[], i: number, e: unknown): BatchResult {
  const error = errorAt(e, i);
  const other = plainError(error.code, { rolledBack: true, failedIndex: i }, `/commands/${i}`, 'batch rolled back; nothing was applied');
  const replies: Reply[] = commands.map((c, j) => ({ ok: false, ...(c.id === undefined ? {} : { id: c.id }), error: j === i ? error : other, warnings: [] }));
  return { doc, reply: { revision: doc.revision, replies }, undo: null };
}

function stale(doc: PuppetDoc, batch: Batch): BatchResult {
  const error = plainError(ERROR_CODES.STALE_REV, { expected: batch.ifRevision, actual: doc.revision }, '/ifRevision', 'reload the puppet and retry');
  const replies: Reply[] = batch.commands.map((c) => ({ ok: false, ...(c.id === undefined ? {} : { id: c.id }), error, warnings: [] }));
  return { doc, reply: { revision: doc.revision, replies }, undo: null };
}

function handlerOf(name: string): LooseDef {
  if (!Object.hasOwn(COMMAND_HANDLERS, name)) throwError(ERROR_CODES.VALIDATION, { expected: 'a command name', got: name }, { path: '/name' });
  return COMMAND_HANDLERS[name as CommandName] as unknown as LooseDef;
}

/**
 * Applies `batch` to a `structuredClone` of `doc`: all commands or none. A stale `ifRevision` or a throw returns the
 * original `doc` with every reply `ok: false` and no undo record; a batch of read-only commands keeps the revision.
 * After the last command `syncImages` makes `images` exactly the referenced ones (from `ctx.images`).
 */
export function applyBatch(doc: PuppetDoc, batch: Batch, ctx: CommandContext): BatchResult {
  if (batch.ifRevision !== undefined && batch.ifRevision !== doc.revision) return stale(doc, batch);
  const draft = structuredClone(doc);
  const outcomes: CommandOutcome[] = [];
  let readOnly = true;
  for (const [i, cmd] of batch.commands.entries()) {
    try {
      const def = handlerOf(cmd.name);
      readOnly &&= def.readOnly === true;
      outcomes.push(def.apply(draft, def.validate(cmd.input, `/commands/${i}/input`), ctx));
    } catch (e) {
      return failed(doc, batch.commands, i, e);
    }
  }
  const replies: Reply[] = batch.commands.map((c, i) => {
    const o = outcomes[i] as CommandOutcome;
    return { ok: true, ...(c.id === undefined ? {} : { id: c.id }), result: o.result, warnings: o.warnings };
  });
  if (readOnly) return { doc, reply: { revision: doc.revision, replies }, undo: null };
  try {
    syncImages(draft, ctx.images);
  } catch (e) {
    return failed(doc, batch.commands, batch.commands.length - 1, e);
  }
  draft.revision = doc.revision + 1;
  return { doc: draft, reply: { revision: draft.revision, replies }, undo: { revision: draft.revision, actor: batch.actor, inverse: invertBatch(outcomes) } };
}
